import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, rememberMe = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: !!rememberMe });
      if (data.mfa_required) {
        return { mfa_required: true, mfa_token: data.mfa_token, user_email: data.user_email, full_name: data.full_name };
      }
      if (data.access_token) {
        localStorage.setItem("cc_access_token", data.access_token);
      }
      setUser(data.user);
      return { user: data.user };
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const completeMfaLogin = async (mfa_token, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/login", { mfa_token, code });
      if (data.access_token) {
        localStorage.setItem("cc_access_token", data.access_token);
      }
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    localStorage.removeItem("cc_access_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, completeMfaLogin, logout, refresh, refreshMe: refresh, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
